import {
  withValidation,
  composeSDKFactories,
  createElementPropsSDKFactory,
  childrenPropsSDKFactory,
  toJSONBase,
  createAccessibilityPropSDKFactory,
} from '@wix/editor-elements-corvid-utils';
import { createComponentSDKModel } from '@wix/editor-elements-integrations/corvid';
import type {
  IAccordionItemPlatformProps,
  IAccordionItemSDKFactory,
  IAccordionItemPropsSDKFactory,
} from '../types';

const accordionItem: IAccordionItemPropsSDKFactory = api => {
  const { metaData } = api;
  const { compId, getParent } = metaData;

  const getItemData = () => {
    const accordionContainer = getParent();
    const itemIndex = accordionContainer?.children.findIndex(
      (child: { uniqueId: string }) => child.uniqueId === compId,
    );
    const item = accordionContainer.items[itemIndex];
    return { accordionContainer, itemIndex, item };
  };

  const sdkProps: ReturnType<IAccordionItemPropsSDKFactory> = {
    get label() {
      const { item } = getItemData();
      return item.label;
    },
    set label(label) {
      const { item, accordionContainer, itemIndex } = getItemData();
      const updatedItem = { ...item, label };
      const items = [...accordionContainer.items];
      items[itemIndex] = updatedItem;
      accordionContainer.items = items;
    },
    get collapsed() {
      const { item, accordionContainer } = getItemData();
      return !accordionContainer.expandedItems.includes(item.id);
    },
    expand: async () => {
      const { item, accordionContainer } = getItemData();
      if (
        !accordionContainer.expandedItems.some((id: string) => id === item.id)
      ) {
        accordionContainer.expandedItems = accordionContainer.multipleExpand
          ? [...accordionContainer.expandedItems, item.id]
          : [item.id];
      }
    },
    collapse: async () => {
      const { item, accordionContainer } = getItemData();
      const isExpanded = accordionContainer.expandedItems.some(
        (id: string) => id === item.id,
      );
      if (isExpanded) {
        accordionContainer.expandedItems =
          accordionContainer.expandedItems.filter(
            (id: string) => id !== item.id,
          );
      }
    },
    toJSON() {
      const { label, collapsed } = sdkProps;
      return {
        ...toJSONBase(metaData),
        label,
        collapsed,
      };
    },
  };

  return sdkProps;
};

const accordionItemPropsSDKFactory = withValidation(accordionItem, {
  type: ['object'],
  properties: {
    label: {
      type: ['string'],
      minLength: 1,
      maxLength: 512,
    },
  },
});

const accessibilityPropsSDKFactory = createAccessibilityPropSDKFactory({
  enableLang: true,
});

export const sdk = composeSDKFactories<
  IAccordionItemPlatformProps,
  IAccordionItemSDKFactory
>([
  createElementPropsSDKFactory(),
  childrenPropsSDKFactory,
  accordionItemPropsSDKFactory,
  accessibilityPropsSDKFactory,
]);

export default createComponentSDKModel(sdk);
